import { ReactNode, useMemo } from 'react'

import { Link } from 'react-router'

import { BreadcrumbBar } from '../../../Breadcrumbs'
import { buildBreadcrumbsData } from '../helpers'
import { SEARCH_RESULT_TYPE, SearchResult, SearchSnippet } from '~/portal/types/search'

interface CommonSearchResultTileProps {
  result: SearchResult
  onClose: () => void
  tileKey: string
  icon: ReactNode
  url: string
  titleContent: ReactNode
}

export const CommonSearchResultTile = ({
  result,
  onClose,
  tileKey,
  icon,
  url,
  titleContent
}: CommonSearchResultTileProps) => {
  const breadcrumbsData = useMemo(() => {
    const showFullBreadcrumbs = result.resultType === SEARCH_RESULT_TYPE.DOCUMENT || result.resultType === SEARCH_RESULT_TYPE.API
    
    return buildBreadcrumbsData({
      productName: result.product.name,
      tileKey,
      parents: result.parents || [],
      title: result.title,
      excludeLastItem: !showFullBreadcrumbs
    })
  }, [result.product.name, result.resultType, result.contentType, tileKey, result.parents, result.title])

  return (
    <Link
      to={url}
      onClick={onClose}
      className="sp-search-result-item group flex flex-row gap-2.5 px-2.5 py-3 cursor-pointer hover:bg-(--sp-color-surface-hover) hover:rounded w-full"
    >
      <div className="text-(--sp-color-text)">
        <div className="flex items-center justify-center w-5 h-5">
          {icon}
        </div>
      </div>
      <div className="flex flex-col w-full overflow-hidden">
        <div
          className="mb-2 font-medium wrap-break-word text-body2 text-(--sp-color-text)"
          role="heading"
          aria-level={3}
        >
          {titleContent}
        </div>
        {breadcrumbsData && <BreadcrumbBar data={breadcrumbsData} />}
        {result.snippet.length ? (
          <span className="mt-1 overflow-hidden wrap-break-word text-body3 text-(--sp-color-text) line-clamp-2">
            {result.snippet.map((snippetItem, snippetIndex) => {
              const key = `snippet-${snippetItem.text}-${snippetIndex}`
              return (
                <span
                  key={key}
                  className={snippetItem.highlight ?
                    'sp-search-result-highlight font-bold bg-(--sp-color-surface-sunken) group-hover:bg-(--sp-color-surface-hover) py-[0.169rem] my-[-0.169rem]'
                    : ''}
                >
                  {snippetItem.text}
                </span>
              )
            })}
          </span>
        ) : null}
      </div>
    </Link>
  )
}

export const HighlightedTitle = ({
  titleSnippet,
  removeLeadingSlash = false,
}: {
  titleSnippet: SearchSnippet[]
  removeLeadingSlash?: boolean
}) => (
  <>
    {titleSnippet.map((snippetItem, snippetIndex) => {
      const key = `titleSnippet-${snippetItem.text}-${snippetIndex}`
      const text =
        removeLeadingSlash && snippetIndex === 0 && snippetItem.text.startsWith('/')
          ? snippetItem.text.slice(1)
          : snippetItem.text

      return (
        <span
          key={key}
          className={snippetItem.highlight ? 'sp-search-result-highlight font-bold bg-(--sp-color-surface-sunken)' : ''}
        >
          {text}
        </span>
      )
    })}
  </>
)